import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, X, Check, FileText, Youtube, LayoutDashboard } from 'lucide-react';
import { apiClient } from '@/lib/api';

interface Project {
  id: string;
  title: string;
  description?: string;
  logline?: string;
  genre?: string;
  format?: string;
  episodes?: number;
  artie_score?: number;
  commercial_viability?: {
    status: string;
    brand_safety: {
      risk_level: string;
      safe_for_brands: boolean;
      concerns: string[];
    };
    category_mapping: {
      primary_categories: string[];
      secondary_categories: string[];
      genre: string;
      themes: string[];
      target_demographics: string[];
    };
    integration_opportunities: {
      potential_level: string;
      visible_logo_opportunities: number;
      product_mention_opportunities: number;
      product_use_opportunities: number;
      natural_integration_points: Array<{
        scene: string;
        opportunity: string;
        type: string;
        quote: string;
      }>;
    };
  };
  craft_assessment?: {
    structure: string;
    hook: string;
    characters: string;
    dialogue: string;
    payoff: string;
    overall_craft_level: string;
    specific_strengths: string[];
    specific_weaknesses: string[];
  };
  reach_analysis?: {
    expected_reach_range: {
      low: number;
      mid: number;
      high: number;
    };
  };
  assets?: Array<{
    id: string;
    url: string;
    content_type?: string;
    deleted_at?: string;
  }>;
}

interface Campaign {
  id: string;
  name: string;
  requirements: {
    suitability_filters: string[];
    preferred_verticals: string[];
  };
}

export default function CampaignProjectReportPage() {
  const { campaignId, projectId } = useParams<{ campaignId: string; projectId: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null>(null);
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [preference, setPreference] = useState<'avoid' | 'neutral' | 'prefer'>('neutral');
  const [requestingScript, setRequestingScript] = useState(false);

  useEffect(() => {
    async function loadData() {
      if (!campaignId || !projectId) return;
      
      try {
        setLoading(true);
        
        // Load campaign data
        const campaignResponse = await apiClient.getCampaign(campaignId);
        setCampaign(campaignResponse);
        
        // Load projects and find the specific one
        const projectsResponse = await apiClient.getCampaignEligibleProjects(campaignId, 1, 100);
        const foundProject = projectsResponse.projects.find((p: any) => p.id === projectId);
        
        if (foundProject) {
          setProject(foundProject);
        }

        // Load existing preference signal
        try {
          const headers: Record<string, string> = {};
          
          // Safari fallback: Add Authorization header if token exists in sessionStorage
          const safariToken = sessionStorage.getItem('safari_auth_token');
          if (safariToken) {
            headers['Authorization'] = `Bearer ${safariToken}`;
          }
          
          const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
          const response = await fetch(`${apiBaseUrl}/api/v1/brand-marketplace/campaigns/${campaignId}/preference-signals`, {
            headers,
            credentials: 'include' // Send cookies with requests
          });
          
          if (response.ok) {
            const signals = await response.json();
            const existingSignal = signals.find((signal: any) => signal.project_id === projectId);
            if (existingSignal) {
              // Map API direction to UI preference
              if (existingSignal.direction === 'up') {
                setPreference('prefer');
              } else if (existingSignal.direction === 'down') {
                setPreference('avoid');
              }
            }
          }
        } catch (error) {
          console.error('Error loading preference signals:', error);
        }
        
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [campaignId, projectId]);

  const handlePreferenceChange = async (newPreference: 'avoid' | 'neutral' | 'prefer') => {
    if (!campaignId || !projectId) return;
    
    try {
      setPreference(newPreference);
      
      if (newPreference === 'avoid' || newPreference === 'prefer') {
        // Create or update preference signal
        const direction = newPreference === 'prefer' ? 'up' : 'down';
        
        const headers: Record<string, string> = {
          'Content-Type': 'application/json'
        };
        
        // Safari fallback: Add Authorization header if token exists in sessionStorage
        const safariToken = sessionStorage.getItem('safari_auth_token');
        if (safariToken) {
          headers['Authorization'] = `Bearer ${safariToken}`;
        }
        
        const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
        const response = await fetch(`${apiBaseUrl}/api/v1/brand-marketplace/campaigns/${campaignId}/preference-signals`, {
          method: 'POST',
          headers,
          credentials: 'include', // Send cookies with requests
          body: JSON.stringify({
            project_id: projectId,
            direction
          })
        });

        if (!response.ok) {
          throw new Error('Failed to save preference');
        }
        
        console.log(`Preference signal saved: ${direction} for project ${projectId}`);
      } else if (newPreference === 'neutral') {
        // Delete the existing preference signal
        const headers: Record<string, string> = {};
        
        // Safari fallback: Add Authorization header if token exists in sessionStorage
        const safariToken = sessionStorage.getItem('safari_auth_token');
        if (safariToken) {
          headers['Authorization'] = `Bearer ${safariToken}`;
        }
        
        const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
        const response = await fetch(`${apiBaseUrl}/api/v1/brand-marketplace/campaigns/${campaignId}/preference-signals/${projectId}`, {
          method: 'DELETE',
          headers,
          credentials: 'include' // Send cookies with requests
        });

        if (!response.ok) {
          throw new Error('Failed to delete preference signal');
        }
        
        console.log(`Preference signal deleted for project ${projectId}`);
      }
    } catch (error) {
      console.error('Error saving preference:', error);
      // Revert the preference on error
      setPreference(preference);
    }
  };

  const handleRequestScriptContext = async () => {
    if (!campaignId || !projectId) return;
    
    try {
      setRequestingScript(true);
      
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      
      // Safari fallback: Add Authorization header if token exists in sessionStorage
      const safariToken = sessionStorage.getItem('safari_auth_token');
      if (safariToken) {
        headers['Authorization'] = `Bearer ${safariToken}`;
      }
      
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
      const response = await fetch(`${apiBaseUrl}/api/v1/brand-marketplace/campaigns/${campaignId}/projects/${projectId}/request-script-context`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          campaign_id: campaignId,
          project_id: projectId,
          request_type: 'script_context'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to request script context');
      }
      
      const result = await response.json();
      console.log('Script context request submitted:', result);
      
      // Show success message or redirect to a confirmation page
      alert('Script context request submitted successfully! You will be contacted with next steps including NDA requirements.');
      
    } catch (error) {
      console.error('Error requesting script context:', error);
      alert('Failed to submit script context request. Please try again.');
    } finally {
      setRequestingScript(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white font-geist flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-neutral-900 mx-auto mb-4"></div>
          <p className="text-neutral-600">Loading project details...</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-white font-geist flex items-center justify-center">
        <div className="text-center">
          <p className="text-neutral-600 mb-4">Project not found</p>
          <Button onClick={() => navigate(`/campaigns/${campaignId}/projects`)}>
            Back to Projects
          </Button>
        </div>
      </div>
    );
  }

  // Get data from artie_score object
  const artieScore = (project as any).artie_score;
  const commercialViability = artieScore?.commercial_viability;
  const integrationOpportunities = artieScore?.integration_opportunities;
  
  const totalOpportunities = integrationOpportunities ?
    (integrationOpportunities.visible_logo_opportunities || 0) +
    (integrationOpportunities.product_mention_opportunities || 0) +
    (integrationOpportunities.product_use_opportunities || 0) : 0;

  const reachAnalysis = artieScore?.reach_analysis || (project as any).reach_analysis;
  const projectedReach = reachAnalysis?.expected_reach_range?.mid || 0;



  // Generate a placeholder image based on project title
  const getProjectImage = (title: string) => {
    const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'];
    const colorIndex = title.length % colors.length;
    return `data:image/svg+xml,${encodeURIComponent(`
      <svg width="400" height="225" xmlns="http://www.w3.org/2000/svg">
        <rect width="400" height="225" fill="${colors[colorIndex]}"/>
        <text x="200" y="112" text-anchor="middle" fill="white" font-family="Arial, sans-serif" font-size="24" font-weight="bold">${title}</text>
      </svg>
    `)}`;
  };

  const coverImageUrl = project.assets
    ?.find((asset) => !asset.deleted_at && (asset.content_type?.startsWith('image/') ?? true))
    ?.url;

  return (
    <div className="min-h-screen bg-white font-geist">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Back Button */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate(`/campaigns/${campaignId}/projects`)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Projects
          </Button>
        </div>
        
        {/* Main Content */}
        <div className="bg-white rounded-lg shadow-sm border">
          {/* Header */}
          <div className="p-6 border-b">
            <h1 className="text-2xl font-semibold">Project Details</h1>
          </div>

          {/* Project Image and Info */}
          <div className="p-6">
            <div className="mb-4">
              <img
                src={coverImageUrl || getProjectImage(project.title)}
                alt={project.title}
                className="w-full h-40 object-cover rounded-lg"
                onError={(e) => {
                  const target = e.currentTarget;
                  target.src = getProjectImage(project.title);
                }}
              />
            </div>

            <div className="mb-4">
              <h3 className="text-xl font-bold mb-2">{project.title}</h3>
              <div className="flex items-center gap-4 text-sm text-neutral-600 mb-2">
                {project.format && <span>{project.format}</span>}
                {project.format && project.episodes && <span>•</span>}
                {project.episodes && <span>{project.episodes} episodes</span>}
                {commercialViability?.category_mapping?.genre && (
                  <>
                    {(project.format || project.episodes) && <span>•</span>}
                    <span>{commercialViability.category_mapping.genre}</span>
                  </>
                )}
              </div>
              {commercialViability?.category_mapping?.genre && (
                <div className="flex items-center gap-2 mb-3">
                  <Badge variant="secondary" className="text-xs">
                    Curated by Artie
                  </Badge>
                </div>
              )}
              <p className="text-sm text-neutral-700 leading-relaxed">
                {project.description || project.logline || 'No description available.'}
              </p>
            </div>

            {/* Why This Matches Your Brief */}
            <div className="mb-6">
              <h4 className="font-semibold mb-3">Why This Matches Your Brief</h4>
              
              <div className="space-y-3">
                {/* Brand Safety Alignment */}
                {campaign?.requirements?.suitability_filters?.includes('Family-friendly content only') &&
                 commercialViability?.brand_safety?.safe_for_brands && (
                  <div className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center mt-0.5">
                      <Check className="w-3 h-3 text-green-600" />
                    </div>
                    <div>
                      <div className="font-medium text-sm">Family-Friendly Content</div>
                      <div className="text-xs text-neutral-600 mt-1">
                        Project is suitable for general audiences, matching your family-friendly requirement
                      </div>
                    </div>
                  </div>
                )}

                {/* Content Vertical Alignment */}
                {campaign?.requirements?.preferred_verticals && commercialViability?.category_mapping?.primary_categories && (
                  (() => {
                    const matchingVerticals = campaign.requirements.preferred_verticals.filter(vertical =>
                      commercialViability.category_mapping.primary_categories.some(category =>
                        category.toLowerCase().includes(vertical.toLowerCase()) ||
                        vertical.toLowerCase().includes(category.toLowerCase())
                      )
                    );
                    
                    return matchingVerticals.length > 0 ? (
                      <div className="flex items-start gap-3">
                        <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center mt-0.5">
                          <Check className="w-3 h-3 text-green-600" />
                        </div>
                        <div>
                          <div className="font-medium text-sm">Content Vertical Match</div>
                          <div className="flex gap-2 mt-1">
                            {matchingVerticals.slice(0, 2).map((vertical, index) => (
                              <Badge key={index} variant="outline" className="text-xs">
                                {vertical}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : null;
                  })()
                )}

                {/* Genre Compatibility */}
                {commercialViability?.category_mapping?.genre && (
                  <div className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center mt-0.5">
                      <Check className="w-3 h-3 text-green-600" />
                    </div>
                    <div>
                      <div className="font-medium text-sm">Genre Compatibility</div>
                      <div className="text-xs text-neutral-600 mt-1">
                        {commercialViability.category_mapping.genre} content with strong commercial appeal
                      </div>
                    </div>
                  </div>
                )}

                {/* Integration Potential */}
                {integrationOpportunities && totalOpportunities > 0 && (
                  <div className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center mt-0.5">
                      <Check className="w-3 h-3 text-green-600" />
                    </div>
                    <div>
                      <div className="font-medium text-sm">Strong Integration Potential</div>
                      <div className="text-xs text-neutral-600 mt-1">
                        {totalOpportunities} placement opportunities identified
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Artie Note */}
            {artieScore?.craft_assessment?.specific_strengths && (
              <div className="mb-6">
                <h4 className="font-semibold mb-3">Artie Note</h4>
                <div className="bg-neutral-50 rounded-lg p-3">
                  <p className="text-sm text-neutral-700 italic">
                    "{artieScore.craft_assessment.specific_strengths[0] || 'Exceptional storytelling and clean narrative placement opportunities.'}"
                  </p>
                </div>
              </div>
            )}

            {/* Placement Opportunities */}
            <div className="mb-6">
              <h4 className="font-semibold mb-3">Placement Opportunities</h4>
              
              {integrationOpportunities ? (
                <>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    {project.episodes && (
                      <div>
                        <div className="text-xs text-neutral-500 uppercase tracking-wide">Episodes</div>
                        <div className="text-2xl font-bold">{project.episodes}</div>
                      </div>
                    )}
                    <div>
                      <div className="text-xs text-neutral-500 uppercase tracking-wide">Total Placements</div>
                      <div className="text-2xl font-bold">{totalOpportunities || 0}</div>
                    </div>
                  </div>

                  {totalOpportunities > 0 ? (
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      {integrationOpportunities.product_mention_opportunities > 0 && (
                        <div>
                          <div className="text-xs text-neutral-500 uppercase tracking-wide">Narrative Placements</div>
                          <div className="font-semibold">
                            {integrationOpportunities.product_mention_opportunities}
                          </div>
                        </div>
                      )}
                      {integrationOpportunities.product_use_opportunities > 0 && (
                        <div>
                          <div className="text-xs text-neutral-500 uppercase tracking-wide">Product Placements</div>
                          <div className="font-semibold">
                            {integrationOpportunities.product_use_opportunities}
                          </div>
                        </div>
                      )}
                      {integrationOpportunities.visible_logo_opportunities > 0 && (
                        <div>
                          <div className="text-xs text-neutral-500 uppercase tracking-wide">Logo Placements</div>
                          <div className="font-semibold">
                            {integrationOpportunities.visible_logo_opportunities}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-sm text-neutral-500 italic">
                      Placement opportunities analysis pending or no specific opportunities identified yet.
                    </div>
                  )}
                </>
              ) : (
                <div className="text-sm text-neutral-500 italic">
                  Placement opportunities analysis not yet available for this project.
                </div>
              )}
            </div>

            {/* Projected Performance */}
            {reachAnalysis?.expected_reach_range && projectedReach > 0 && (
              <div className="mb-6">
                <h4 className="font-semibold mb-3">Projected Performance</h4>
                
                <div className="mb-3">
                  <div className="text-xs text-neutral-500 uppercase tracking-wide">90-Day Projected Reach</div>
                  <div className="flex items-center gap-2">
                    <div className="text-2xl font-bold">
                      {projectedReach >= 1000000 ? `${(projectedReach / 1000000).toFixed(1)}M` :
                       projectedReach >= 1000 ? `${(projectedReach / 1000).toFixed(0)}K` :
                       projectedReach.toLocaleString()}
                    </div>
                    <Youtube className="w-4 h-4 text-red-500" />
                    <span className="text-xs text-neutral-500">YouTube</span>
                  </div>
                  {reachAnalysis.expected_reach_range.low && reachAnalysis.expected_reach_range.high && (
                    <div className="text-xs text-neutral-500 mt-1">
                      Range: {reachAnalysis.expected_reach_range.low >= 1000000 ?
                        `${(reachAnalysis.expected_reach_range.low / 1000000).toFixed(1)}M` :
                        reachAnalysis.expected_reach_range.low >= 1000 ?
                        `${(reachAnalysis.expected_reach_range.low / 1000).toFixed(0)}K` :
                        reachAnalysis.expected_reach_range.low.toLocaleString()} - {
                        reachAnalysis.expected_reach_range.high >= 1000000 ?
                        `${(reachAnalysis.expected_reach_range.high / 1000000).toFixed(1)}M` :
                        reachAnalysis.expected_reach_range.high >= 1000 ?
                        `${(reachAnalysis.expected_reach_range.high / 1000).toFixed(0)}K` :
                        reachAnalysis.expected_reach_range.high.toLocaleString()}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Your Preference */}
            <div className="mb-6">
              <div className="text-xs text-neutral-500 uppercase tracking-wide mb-3">Your Preference</div>
              <div className="flex gap-2">
                <Button
                  variant={preference === 'avoid' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handlePreferenceChange('avoid')}
                  className="flex-1"
                >
                  {preference === 'avoid' && <X className="w-3 h-3 mr-1" />}
                  Avoid
                </Button>
                <Button
                  variant={preference === 'neutral' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handlePreferenceChange('neutral')}
                  className="flex-1"
                >
                  Neutral
                </Button>
                <Button
                  variant={preference === 'prefer' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handlePreferenceChange('prefer')}
                  className="flex-1"
                >
                  Prefer
                </Button>
              </div>
            </div>

            {/* Navigation Actions */}
            <div className="mb-6 flex gap-3">
              <Button
                variant="outline"
                onClick={() => navigate(`/campaigns/${campaignId}/projects`)}
                className="flex-1"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Projects
              </Button>
              <Button
                onClick={() => navigate('/brand-dashboard')}
                className="flex-1 bg-neutral-900 hover:bg-neutral-800 text-white"
              >
                <LayoutDashboard className="w-4 h-4 mr-2" />
                Go to Dashboard
              </Button>
            </div>

            {/* Request Script Context */}
            <div className="border-t pt-4">
              <Button
                className="w-full"
                variant="outline"
                onClick={handleRequestScriptContext}
                disabled={requestingScript}
              >
                <FileText className="w-4 h-4 mr-2" />
                {requestingScript ? 'Submitting Request...' : 'Request Script Context'}
              </Button>
              <p className="text-xs text-neutral-500 mt-2 text-center">
                Gated access requiring NDA acceptance • Provides additional narrative context without exposing full scripts
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}